import React, { useState } from 'react';
import { X, Shield, Target, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ABOUT_WASSLA_CONTENT } from '../content/aboutWassla';
import WasslaLogo from './WasslaLogo';
import './AboutWasslaModal.css';

/**
 * AboutWasslaModal - Professional About modal with EN/AR support
 * Features glassmorphism design, smooth animations, and accessibility
 */
export default function AboutWasslaModal({ isOpen, onClose }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const content = isRTL ? ABOUT_WASSLA_CONTENT.ar : ABOUT_WASSLA_CONTENT.en;
  const [logoError, setLogoError] = useState(false);

  // Close on escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Icon mapping for sections
  const sectionIcons = {
    0: Shield,    // Why Wassla
    1: CheckCircle, // Our Values
    2: Target,    // Our Mission
  };

  return (
    <div 
      className="about-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="about-modal-title"
      data-testid="about-wassla-modal"
    >
      <div 
        className="about-modal-container"
        dir={isRTL ? 'rtl' : 'ltr'}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Wassla Logo (real logo, no profile icon) */}
        <div className="about-modal-header">
          <div className="about-modal-logo">
            <WasslaLogo size="large" alt="Wassla Logo" />
          </div>
          <div className="about-title-wrapper">
            <h2 id="about-modal-title" className="about-modal-title" data-testid="about-modal-title">
              {content.title}
            </h2>
          </div>
          <button 
            className="about-modal-close"
            onClick={onClose}
            aria-label={t('rolesele.close')}
            data-testid="about-modal-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tagline */}
        <p className="about-modal-tagline">{content.tagline}</p>

        {/* Content Sections */}
        <div className="about-modal-content">
          {content.sections.map((section, index) => {
            const IconComponent = sectionIcons[index] || Shield;
            
            return (
              <div key={index} className="about-section">
                <div className="about-section-header">
                  <div className="about-section-icon">
                    <IconComponent size={18} />
                  </div>
                  <h3 className="about-section-heading">{section.heading}</h3>
                </div>
                
                {section.text && (
                  <p className="about-section-text">{section.text}</p>
                )}
                
                {section.bullets && (
                  <ul className="about-section-bullets">
                    {section.bullets.map((bullet, i) => (
                      <li key={i}>
                        <CheckCircle size={14} className="bullet-icon" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="about-modal-footer">
          <button 
            className="about-modal-btn"
            onClick={onClose}
            data-testid="about-modal-close-action"
          >
            {content.button}
          </button>
        </div>
      </div>
    </div>
  );
}
